import {
    getFolderLocation,
    markNotificationAsRead,
    entityByID,
} from "@rpforms/shared";
import {
    SmallButton,
    SmallDangerButton,
    SmallPrimaryButton,
} from "@rpforms/shared/build/components/layout/Button";
import { push } from "connected-react-router";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

const RowIcon = styled.div`
    margin-right: 1rem;
    color: #6e6e6e;

    small {
        margin-left: 0.5rem;
    }
`;

const formatAddress = (address: any) => {
    if (!address) return "";
    const parts = [address.street, `${address.postalcode} ${address.city}`].filter(Boolean);
    return parts.length > 0 ? `(${parts.join(", ")})` : "";
};

const getEntityById = (entities: any, entityId: number) => {
    return (
        entities.entities.find((e: any) => e.id === entityId) ||
        entities.autocomplete.find((e: any) => e.id === entityId)
    );
};

const handleEntityClick = (entityId: number, customerId: number, dispatch: any) => {
    dispatch(push(`/master_data/customers/view-${customerId}?entity=${entityId}`));
};

export const NotificationRow = ({ notification }) => {
    const dispatch = useDispatch();
    const entities = useSelector((state: any) => state.entities);

    const isBatteryMaintenanceNotification =
        notification.notification_type === "one_week_battery_maintenance" ||
        notification.notification_type === "one_month_battery_maintenance";

    useEffect(() => {
        if (isBatteryMaintenanceNotification && notification.resource?.entity_id) {
            const entityId = notification.resource.entity_id;

            const entityInStore = getEntityById(entities, entityId);

            if (!entityInStore) {
                dispatch(entityByID(entityId));
            }
        }
    }, [
        isBatteryMaintenanceNotification,
        notification.resource?.entity_id,
        entities.entities,
        entities.autocomplete,
    ]);

    const dateOfNotification = () => {
        const currentDate = new Date().getTime();
        const notifDate = new Date(notification.created_at).getTime();
        const timeBetween = currentDate - notifDate;
        const daysBetween = Math.ceil(timeBetween / (1000 * 60 * 60 * 24));

        let message = "";

        if (daysBetween < 2) {
            message = "vor einem Tag";
        } else if (daysBetween > 7) {
            message = new Date(notification.created_at).toLocaleDateString("de-DE");
        } else {
            message = `vor ${daysBetween} Tagen`;
        }

        return message;
    };

    const actionLink = () => {
        switch (notification.resource_type) {
            case "Customer":
                dispatch(push(`/master_data/customers/view-${notification.resource.id}`));
                break;
            case "Entity":
                if (notification.url) {
                    dispatch(push(`${notification.url}`));
                } else {
                    dispatch(
                        push(`/master_data/customers/view-${notification.resource.customer_id}`)
                    );
                }
                break;
            case "FormEntry":
                dispatch(push(`/entries?ids=${notification.resource_id}`));
                break;
            case "Folder":
                if (notification.url) {
                    dispatch(push(`${notification.url}`));
                } else {
                    dispatch(
                        push(`/master_data/customers/view-${notification.resource.customer_id}`)
                    );
                }
                break;
        }
    };

    const actionData = () => {
        const result = {
            name: "Öffnen",
            icon: "",
        };
        switch (notification.resource_type) {
            case "Customer":
                result.name = "Kunde öffnen";
                result.icon = "fa fa-users-rectangle";
                break;
            case "Entity":
                result.name = "Objekt öffnen";
                result.icon = "fa fa-industry";
                break;
            case "FormEntry":
                result.name = "Protokoll öffnen";
                result.icon = "fa fa-file-text";
                break;
            case "Folder":
                result.name = "Ansehen";
                result.icon = "fa fa-table";
                break;
        }
        return result;
    };

    const actionLinkText = () => {
        const r = actionData();
        return (
            <>
                <i className={r.icon} /> {r.name}
            </>
        );
    };

    const renderBatteryMaintenanceInfo = () => {
        if (
            !isBatteryMaintenanceNotification ||
            !notification.resource ||
            !notification.resource.entity_id
        ) {
            return null;
        }

        const entityId = notification.resource.entity_id;
        const entity = getEntityById(entities, entityId);

        if (!entity) {
            return (
                <div style={{ marginBottom: "0.5rem", fontSize: "0.85rem", color: "#6c757d" }}>
                    <strong>Objekt:</strong> <em>Wird geladen...</em>
                </div>
            );
        }

        const address = entity.address;
        const addressString = formatAddress(address);

        return (
            <div style={{ marginBottom: "0.5rem", fontSize: "0.85rem", color: "#6c757d" }}>
                <div>
                    <strong>Objekt: </strong>
                    <span
                        onClick={() => handleEntityClick(entityId, entity.customer_id, dispatch)}
                        style={{
                            cursor: "pointer",
                            textDecoration: "underline",
                            fontWeight: "bold",
                        }}
                    >
                        {entity.name} {address && addressString}
                    </span>{" "}
                </div>
            </div>
        );
    };

    return (
        <div
            style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
            }}
            data-id={notification.id}
        >
            <i className="fa fa-file-text-o" />
            <RowIcon></RowIcon>
            <div style={{ flexGrow: 2 }}>
                <small style={{}}>{notification.message}</small>
                {renderBatteryMaintenanceInfo()}
            </div>
            <div>
                <SmallButton
                    onClick={() => {
                        actionLink();
                    }}
                >
                    {actionLinkText()}
                </SmallButton>
                <SmallPrimaryButton
                    onClick={async () => {
                        const req: any = await dispatch(
                            getFolderLocation({
                                resource_id: notification.resource_id,
                                resource_type: notification.resource_type,
                            })
                        );
                        dispatch(
                            push(
                                "/folders?open=" + encodeURIComponent(JSON.stringify(req.data))
                            )
                        );
                    }}
                >
                    <i className="fa fa-folder-open" /> Ordnerstruktur
                </SmallPrimaryButton>
                <SmallDangerButton
                    onClick={async () => {
                        dispatch(markNotificationAsRead(notification.id));
                    }}
                >
                    Abschließen
                </SmallDangerButton>
                <small style={{ marginBottom: "-3px" }}>{dateOfNotification()}</small>
            </div>
        </div>
    );
};

export default NotificationRow;
